import { hushBlow, hushed } from './panoply-paladin.ts';
import { FEAR, NIGHTMARES, PLAN, SHELL, STRUCTURES, TIERS, WALL_CLASS, WAVE, WITCH, type NightmareType, type StructureCategory } from '@somnhold/content';
import { blockingAtCell, cache, cellCenter, cellHex, cellId, classFlow, flowNext, gridW, heroWalkable, spotFlow, touchGrid, trampleFlow, trapAtCell } from '../grid.ts';
import { DIRS, hexDist, hexLen, hexToWorld } from '../hex.ts';
import { isInside } from '../walls.ts';
import { ANGLE_STEPS, cosA, dist2, iatan2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import { T_KEEP, type BattleState, type Bot, type Enemy, type Hero, type Structure } from '../types.ts';
import {
  cellIdAt,
  enemyCell,
  damageEnemy,
  damageHero,
  damageKeep,
  damageStructure,
  ev,
  findHero,
  findStructure,
  isFlying,
  spawnEnemy,
} from './combat.ts';
import { blowOf } from './kits.ts';
import { chaseHero, wardenTick } from './dungeon.ts';
import { expChase } from './expedition.ts';
import { draggerTick } from './plunder.ts';
import { mazeChase } from './maze.ts';
import { drawnBy } from './bots.ts';
import { dragonTick } from './dragon.ts';
import { engineFor } from './plan.ts';
import { unsleepingStatus, unsleepingStrike, unsleepingTick } from './unsleeping.ts';
import { facetakerTick } from './facetaker.ts';
import { herdChase, herdStampede } from './herd.ts';
import { inertKind, simaBossTick } from './sima-bosses.ts';
import { wellChase } from './well.ts';
import { drinkerTick } from './drinker.ts';
import { fairDrag } from './fair.ts';
import { drownedGrab, drownedHold } from './tide.ts';
import { uroTick } from './hunt.ts';
import { firesAsTower } from './towers.ts';
import { sunkenMove } from './sunken.ts';

/** Reach to hit the keep: it is a big target. */
const KEEP_REACH = 600;
const STRUCT_REACH = 450;

/** Moves an entity toward (tx,ty) by up to `px`; returns true if it arrived. */
export const stepToward = (o: { x: number; y: number; facing: number }, tx: number, ty: number, px: number): boolean => {
  const dx = tx - o.x;
  const dy = ty - o.y;
  if (dx === 0 && dy === 0) return true;
  const d = isqrt(dx * dx + dy * dy);
  o.facing = iatan2(dy, dx);
  if (d <= px) {
    o.x = tx;
    o.y = ty;
    return true;
  }
  o.x += idiv(dx * px, d);
  o.y += idiv(dy * px, d);
  return false;
};

/** Pixels (fixed units) to move this tick, with sub-tick accumulation. */
const movePx = (e: Enemy, tick = -1): number => {
  let sp = e.speed;
  if (e.slowTicks > 0 && e.slowBp > 0) sp = mulBp(sp, 10000 - e.slowBp);
  // Under a bannerman's banner it marches faster.
  if (tick >= 0 && (e.inspired === tick || (e.rallied ?? 0) > tick)) sp = mulBp(sp, 10000 + (NIGHTMARES.bannerman.banner?.speedBp ?? 0));
  e.moveAcc += sp;
  const px = idiv(e.moveAcc, 12);
  e.moveAcc -= px * 12;
  return px;
};

const heroAlive = (h: Hero | null): h is Hero => !!h && h.downTicks === 0;

export const updateEnemies = (s: BattleState): void => {
  const engaged = new Map<number, number>();
  for (const e of s.enemies) {
    if (e.hp > 0 && e.tgtKind === 1) engaged.set(e.tgtId, (engaged.get(e.tgtId) ?? 0) + 1);
  }
  // A bannerman's banner: the nightmares around it march faster and hit harder this tick.
  for (const b of s.enemies) {
    const ban = NIGHTMARES[b.kind].banner;
    if (!ban || b.hp <= 0) continue;
    for (const o of s.enemies) if (o.id !== b.id && o.hp > 0 && withinRange(b.x, b.y, o.x, o.y, ban.radius)) o.inspired = s.tick;
  }
  // Iterate a fixed-length snapshot: enemies spawned this tick (summons) act from next tick.
  const n = s.enemies.length;
  for (let i = 0; i < n; i++) {
    const e = s.enemies[i] as Enemy;
    if (e.hp <= 0) continue;
    e.animT++;
    tickStatus(s, e);
    if (e.hp <= 0) continue;
    if (e.kind === 'boneWall' || inertKind(e.kind)) continue;
    // A sapper under the wall: it digs, then comes up inside.
    if (e.phase === 1 && NIGHTMARES[e.kind].burrows) {
      if (--e.timer <= 0) {
        e.x = e.digX ?? e.x;
        e.y = e.digY ?? e.y;
        e.phase = 2;
        e.next = -1;
        e.anim = 'walk';
        ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'emerge' });
      }
      continue;
    }
    // A shell serpent: under the ground it can't be reached; it comes up by a tower inside the walls.
    if (NIGHTMARES[e.kind].tunnels && e.phase !== 2 && s.mode !== 'dungeon') {
      serpentDig(s, e);
      continue;
    }
    // The dragon of the Burn is never stunned, by whatever way it came (a mace, a freeze, a form): a dry blow instead.
    if (e.kind === 'dragon' && e.stun > 0) {
      e.stun = 0;
      e.dry = true;
    }
    if (e.stun > 0) {
      e.stun--;
      e.anim = 'stun';
      continue;
    }
    // The Old Uro grazes and charges its own way (it never strikes).
    if (e.kind === 'uro') {
      uroTick(s, e);
      continue;
    }
    if (e.anim === 'stun' || (e.anim === 'hit' && e.animT > 2) || (e.anim === 'cast' && e.animT > 12)) e.anim = 'walk';
    if (e.cd > 0) e.cd--;
    // The Maw and its light's stalk stay where they are (battle/maw.ts bites and drags).
    if (e.kind === 'maw' || e.kind === 'lure') {
      if (e.anim === 'walk') e.anim = 'idle';
      continue;
    }
    // The Drowned Dragon walks and claws its own way (battle/sunken.ts breathes and is crushed).
    if (e.kind === 'sunken') {
      sunkenMove(s, e, movePx(e, s.tick), (h) => attackHero(s, e, h));
      continue;
    }
    // Era I's powers (docs/CRIATURAS.md): they work the same on the bastion, out on an incursion and down a crypt.
    const nd = NIGHTMARES[e.kind];
    if (nd.mends) mendTick(s, e, nd.mends);
    if (nd.roar) roarTick(s, e, nd.roar);
    // Era III's (docs/CRIATURAS.md): the giant's rocks, stomp and waking, the weaver's silk, the oracle's bubbles, the wyrmling's fire.
    if (nd.wakes) wakeTick(s, e, nd.wakes);
    if (nd.hurls) hurlTick(s, e, nd.hurls);
    if (nd.stomp) stompTick(s, e, nd.stomp);
    if (nd.weaves) weaveTick(s, e, nd.weaves);
    if (nd.wards) wardTick(s, e, nd.wards);
    if (nd.breathes) breathTick(s, e, nd.breathes);
    // Era IV's: the frost colossus's breath; the surfaced serpent goes from tower to tower.
    if (nd.freezes) freezeTick(s, e, nd.freezes);
    // Era V's: the shellback's loads dropping off, the dreamer's open eyes.
    if (nd.carries) carryTick(s, e, nd.carries);
    if (nd.dreams) dreamTick(s, e, nd.dreams);
    if (nd.tunnels && s.mode !== 'dungeon' && serpentHunt(s, e)) continue;
    if (nd.shootsHeroes && e.taunter === 0 && !s.wave.lastStand && !(e.hushUntil && hushed(s, e)) && slingTick(s, e, nd.shootsHeroes)) continue;
    if (s.mode === 'dungeon') {
      if (s.dungeon?.exp && expChase(s, e, movePx(e, s.tick), (h) => attackHero(s, e, h))) continue;
      if (s.dungeon?.maze && mazeChase(s, e, movePx(e, s.tick), (h) => attackHero(s, e, h))) continue;
      // A Chasm guardian's rule: drained, swollen, reading, drawing breath or under the ground, it does nothing else.
      if (s.dungeon?.sima && simaBossTick(s, e)) continue;
      if (e.kind === 'warden') wardenTick(s, e, (x) => movePx(x, s.tick), (h) => attackHero(s, e, h), (b) => attackBot(s, e, b));
      else if (e.kind === 'drinker') drinkerTick(s, e, (x) => movePx(x, s.tick), (px) => chaseHero(s, e, px, (h) => attackHero(s, e, h), (b) => attackBot(s, e, b)));
      else chaseHero(s, e, (x) => movePx(x, s.tick), (h) => attackHero(s, e, h), (b) => attackBot(s, e, b));
      continue;
    }
    // A dragger hauls the plunder's caches away and never fights.
    // A Drowned with a hero in its grip walks them out to sea (La Orilla).
    if (e.grab && drownedHold(s, e, movePx(e, s.tick))) continue;
    if (e.kind === 'dragger') {
      if (s.fair) fairDrag(s, e, movePx(e, s.tick));
      else draggerTick(s, e, movePx(e, s.tick));
      continue;
    }
    // The Prestacaras: standing still a moment, then its borrowed kit (none while bare), and after the heroes.
    if (e.mask) {
      const f = facetakerTick(s, e);
      if (f === 'hold') continue;
      if (f === null && e.un && unsleepingTick(s, e)) continue;
      chaseHero(s, e, (x) => movePx(x, s.tick), (h) => attackHero(s, e, h), (b) => attackBot(s, e, b));
      continue;
    }
    // The saga's dragon flies, lands and fights its own way; a saga boss comes for the heroes, not the node.
    if (e.kind === 'dragon') {
      dragonTick(s, e, (x) => movePx(x, s.tick), (h) => attackHero(s, e, h));
      continue;
    }
    // On a plunder there is no node to break: they hunt the heroes (and a hero with full hands can't strike back).
    // On the drive, neither: they hunt the herd, or whichever hero is closer.
    if (s.herd && herdChase(s, e, movePx(e, s.tick))) continue;
    // Out of the well: at the houses, or whichever hero is closer (there is no node to go for).
    if (s.well && wellChase(s, e, movePx(e, s.tick))) continue;
    if (s.bossId === e.id || s.plunder || s.herd || s.collapse || s.trap || s.well || s.fair || s.dragon) {
      chaseHero(s, e, (x) => movePx(x, s.tick), (h) => attackHero(s, e, h), (b) => attackBot(s, e, b));
      continue;
    }
    // One of the Unsleeping (docs/ANTIHEROES.md): his challenge, charge, ultimate and flight; else he walks in as any.
    if (e.un && unsleepingTick(s, e)) continue;
    if (e.kind === 'witch') witchTick(s, e);
    else if (e.kind === 'shell') shellTick(s, e);
    if (isFlying(e)) flyerTick(s, e);
    else groundTick(s, e, engaged);
  }
};

const tickStatus = (s: BattleState, e: Enemy): void => {
  if (e.un) unsleepingStatus(s, e);
  if (e.acidTicks && e.acidTicks > 0) e.acidTicks--;
  if (e.grounded && e.grounded > 0) e.grounded--;
  if (e.markTicks && e.markTicks > 0) e.markTicks--;
  if (e.blind && e.blind > 0) e.blind--;
  if (e.linkTicks && e.linkTicks > 0) e.linkTicks--;
  if (e.duelTicks && e.duelTicks > 0) e.duelTicks--;
  if (e.seal && e.seal > 0) {
    e.seal--;
    // The burning seal: light on it every few ticks while it lasts.
    if (e.sealBurn && e.seal % e.sealBurn.every === 0) damageEnemy(s, e, e.sealBurn.damage, { pid: e.sealBurn.pid, type: 'holy' });
    if (e.seal === 0) e.sealBurn = undefined;
  }
  if (e.slowTicks > 0) {
    e.slowTicks--;
    if (e.slowTicks === 0) e.slowBp = 0;
  }
  if (e.tauntTicks > 0) {
    e.tauntTicks--;
    if (e.tauntTicks === 0) e.taunter = 0;
  }
  if (e.dotTicks > 0) {
    e.dotTicks--;
    if (e.dotEvery > 0 && e.dotTicks % e.dotEvery === 0) {
      damageEnemy(s, e, e.dotDmg, { pid: e.lastPid, ignoresArmor: true, type: e.dotType ?? 'fire', dot: true });
    }
  }
  // A troll mends fast, unless it burns (a fire dot on it) or fire touched it lately (`scorch`: every
  // fire blow, burn tick or burning zone, set in damageEnemy). Until 2026-11-04 any running dot stopped
  // it (a bleed, a poison) and burning zones didn't: docs/CRIATURAS.md says only fire.
  const rg = NIGHTMARES[e.kind].regen;
  const burning = e.dotTicks > 0 && (e.dotType ?? 'fire') === 'fire';
  if (rg && e.hp < e.maxHp && !burning && s.tick - (e.scorch ?? -100000) >= rg.burnTicks) {
    e.regenAcc += e.maxHp * rg.bpPerSec;
    const heal = idiv(e.regenAcc, 120000);
    if (heal > 0) {
      e.regenAcc -= heal * 120000;
      e.hp = imin(e.maxHp, e.hp + heal);
    }
  }
  const tier = TIERS[e.tier];
  // (The dragon of the Burn mends only by eating spectres: docs/UNICAS.md §4.4.)
  if (tier.regenBpPerSec > 0 && e.hp > 0 && e.hp < e.maxHp && s.tick - e.lastHit >= tier.regenDelay && e.kind !== 'dragon') {
    // regen bp per second → per tick, accumulated in 1/120000 of max HP
    e.regenAcc += e.maxHp * tier.regenBpPerSec;
    const heal = idiv(e.regenAcc, 120000);
    if (heal > 0) {
      e.regenAcc -= heal * 120000;
      e.hp += heal;
      if (e.hp > e.maxHp) e.hp = e.maxHp;
    }
  }
};

const attackHero = (s: BattleState, e: Enemy, h: Hero): void => {
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (e.cd > 0) return;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  const d = NIGHTMARES[e.kind];
  const blow = blowOf(s, e, d.structureHunter ? idiv(e.damage, 2) : d.siege ? idiv(e.damage, 3) : e.damage);
  if (e.un) unsleepingStrike(s, e, h, blow);
  else damageHero(s, h, blow, e);
  if (h.downTicks > 0) return;
  if (d.curses) {
    h.curse = d.curses.ticks;
    h.curseBp = d.curses.healBp;
  }
  if (d.knockback) knockBack(s, e, h, d.knockback);
  // A Drowned takes hold.
  if (e.kind === 'drowned') drownedGrab(s, e, h);
  // An ash watcher tears the hero's boons away and mends on them.
  if (d.stealsBoons) {
    const had = (h.shield ?? 0) > 0 || (h.ward ?? 0) > 0 || (h.inspire ?? 0) > 0 || (h.renew ?? 0) > 0 || (h.blessed ?? 0) > 0 || (h.roar ?? 0) > 0;
    if (had) {
      h.shield = 0;
      h.shieldTicks = 0;
      h.ward = 0;
      h.inspire = 0;
      h.renew = 0;
      h.blessed = 0;
      h.roar = 0;
      e.hp = imin(e.maxHp, e.hp + mulBp(e.maxHp, d.stealsBoons.healBp));
      ev(s, { t: 'shot', from: h.id, to: e.id, kind: 'steal', x: h.x, y: h.y, tx: e.x, ty: e.y });
    }
  }
  // A fallen sentinel: every so often its blow is a shield bash that stuns.
  if (d.bashes && s.tick >= (e.bashAt ?? 0) && (h.frenzy ?? 0) <= 0) {
    e.bashAt = s.tick + d.bashes.every;
    h.stun = imax(h.stun, d.bashes.ticks);
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'bash' });
  }
  if (d.poisons) {
    h.poison = imax(h.poison ?? 0, d.poisons.ticks);
    h.poisonDmg = d.poisons.dmg;
  }
  // A moth wraps it: asleep, unless it only just woke (nothing puts a barbarian in a frenzy to sleep).
  if (d.sleeps && !((h.sleep ?? 0) > 0) && (h.frenzy ?? 0) <= 0 && s.tick - (h.wokeAt ?? -100000) >= d.sleeps.immune) {
    h.sleep = d.sleeps.ticks;
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'sleep' });
  }
};

/** The ogre's sweep: the hero slides back along the blow (see `shoveTick`); nothing moves a barbarian in a frenzy, nor a hero thrown only a moment ago. */
export const knockBack = (s: BattleState, e: Enemy, h: Hero, k: { far: number; ticks: number; immune: number }): void => {
  if ((h.frenzy ?? 0) > 0) return;
  if (s.tick - (h.shovedAt ?? -100000) < k.immune) return;
  const dx = h.x - e.x;
  const dy = h.y - e.y;
  const d = isqrt(dx * dx + dy * dy);
  if (d === 0) return;
  h.shove = k.ticks;
  h.shovedAt = s.tick;
  h.shoveX = idiv(dx * k.far, d * k.ticks);
  h.shoveY = idiv(dy * k.far, d * k.ticks);
};

/** The mourner: every so often, threads of ink mend the wounded nightmares around it (never itself). */
const mendTick = (s: BattleState, e: Enemy, m: { every: number; radius: number; bp: number }): void => {
  e.timer++;
  if (e.timer < m.every) return;
  const hurt = s.enemies.filter((o) => o.id !== e.id && o.hp > 0 && o.hp < o.maxHp && o.kind !== 'boneWall' && withinRange(e.x, e.y, o.x, o.y, m.radius));
  if (hurt.length === 0) return;
  e.timer = 0;
  e.anim = 'cast';
  e.animT = 0;
  for (const o of hurt) {
    const got = imin(o.maxHp - o.hp, imax(1, mulBp(o.maxHp, m.bp)));
    o.hp += got;
    ev(s, { t: 'mend', from: e.id, id: o.id, amount: got, x: o.x, y: o.y });
  }
};

/** The ogre: with a hero close, it roars every so often and the heroes around lose their nerve. */
const roarTick = (s: BattleState, e: Enemy, r: { every: number; radius: number; ticks: number }): void => {
  if (e.timer < r.every) e.timer++;
  if (e.timer < r.every) return;
  const near = s.heroes.filter((h) => h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, r.radius));
  if (near.length === 0) return;
  e.timer = 0;
  e.anim = 'cast';
  e.animT = 0;
  // Nothing holds a barbarian in a frenzy, and a hero just over a fright holds its nerve a while (FEAR).
  for (const h of near) {
    if ((h.frenzy ?? 0) > 0 || s.tick < (h.brave ?? 0)) continue;
    h.fear = imax(h.fear ?? 0, r.ticks);
    h.brave = s.tick + r.ticks + FEAR.graceTicks;
  }
  ev(s, { t: 'boom', x: e.x, y: e.y, r: r.radius, kind: 'roar' });
  if (s.herd) herdStampede(s, e);
};

/** The giant: below half health it wakes, walks faster and starts roaring at once. */
const wakeTick = (s: BattleState, e: Enemy, w: { atBp: number; speedBp: number; roar: { every: number; radius: number; ticks: number } }): void => {
  if (e.phase === 0 && e.hp * 10000 <= e.maxHp * w.atBp) {
    e.phase = 1;
    e.speed = mulBp(e.speed, w.speedBp);
    e.timer = w.roar.every;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: w.roar.radius, kind: 'wake' });
  }
  if (e.phase === 1) roarTick(s, e, w.roar);
};

/**
 * The mangonel: outside the walls, with a wall, gate or rampart in its throw (a tower when no wall is), it
 * stands and lobs a rock at the nearest every so often. Returns whether it stood.
 */
const lobTick = (s: BattleState, e: Enemy, l: { range: number; every: number; dmg: number }): boolean => {
  if (insideCell(s, enemyCell(s, e))) return false;
  const t = lobTarget(s, e, l.range);
  if (!t) return false;
  const c = centerOf(t);
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  e.tgtKind = 2;
  e.tgtId = t.id;
  if (e.cd > 0) return true;
  e.cd = l.every;
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'shot', from: e.id, to: t.id, kind: 'mangonelRock', x: e.x, y: e.y, tx: c.x, ty: c.y });
  damageStructure(s, t, blowOf(s, e, e.hushUntil ? hushBlow(s, e, l.dmg) : l.dmg), e);
  return true;
};

/** The giant: every so often it tears up a rock and hurls it at the nearest tower in its throw. */
const hurlTick = (s: BattleState, e: Enemy, r: { range: number; every: number; dmg: number }): void => {
  if ((s.tick - e.born) % r.every !== 12) return;
  const t = nearestTowerWithin(s, e, r.range);
  if (!t) return;
  const c = hexToWorld(t.q, t.r);
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'shot', from: e.id, to: t.id, kind: 'giantRock', x: e.x, y: e.y, tx: c.x, ty: c.y });
  damageStructure(s, t, blowOf(s, e, e.hushUntil ? hushBlow(s, e, r.dmg) : r.dmg), e);
};

/** The giant's stomp: the heroes around it are thrown off their feet. */
const stompTick = (s: BattleState, e: Enemy, st: { every: number; radius: number; ticks: number; dmg: number }): void => {
  if ((s.tick - e.born) % st.every !== st.every - 1) return;
  const near = s.heroes.filter((h) => h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, st.radius));
  if (near.length === 0) return;
  e.anim = 'attack';
  e.animT = 0;
  for (const h of near) {
    damageHero(s, h, blowOf(s, e, st.dmg), e);
    if (h.downTicks === 0 && (h.frenzy ?? 0) <= 0) h.stun = imax(h.stun, st.ticks);
  }
  ev(s, { t: 'boom', x: e.x, y: e.y, r: st.radius, kind: 'stomp' });
};

/** The weaver: every so often it cocoons the nearest tower in reach that isn't already wrapped. */
const weaveTick = (s: BattleState, e: Enemy, w: { range: number; every: number; ticks: number }): void => {
  if ((s.tick - e.born) % w.every !== w.every - 1) return;
  let best: Structure | null = null;
  let bd = w.range * w.range + 1;
  for (const st of s.structures) {
    if (st.destroyed || (st.cocoon ?? 0) > 0 || !firesAsTower(st) || st.type === 'beacon') continue;
    const c = hexToWorld(st.q, st.r);
    const dd = dist2(e.x, e.y, c.x, c.y);
    if (dd < bd) {
      bd = dd;
      best = st;
    }
  }
  if (!best) return;
  const c = hexToWorld(best.q, best.r);
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  e.anim = 'cast';
  e.animT = 0;
  best.cocoon = w.ticks;
  ev(s, { t: 'shot', from: e.id, to: best.id, kind: 'silk', x: e.x, y: e.y, tx: c.x, ty: c.y });
};

/** The ink oracle: every so often a bubble over each nightmare around it (never itself). */
const wardTick = (s: BattleState, e: Enemy, w: { every: number; radius: number; hp: number }): void => {
  if (e.timer < w.every) e.timer++;
  if (e.timer < w.every) return;
  const near = s.enemies.filter((o) => o.id !== e.id && o.hp > 0 && o.kind !== 'boneWall' && (o.ward ?? 0) < w.hp && withinRange(e.x, e.y, o.x, o.y, w.radius));
  if (near.length === 0) return;
  e.timer = 0;
  e.anim = 'cast';
  e.animT = 0;
  for (const o of near) {
    o.ward = w.hp;
    ev(s, { t: 'mend', from: e.id, id: o.id, amount: 0, x: o.x, y: o.y });
  }
  ev(s, { t: 'boom', x: e.x, y: e.y, r: w.radius, kind: 'ward' });
};

/** The Burn wyrmling: a hero in reach, and it breathes a line of fire across the ground through it. */
const breathTick = (s: BattleState, e: Enemy, b: { every: number; range: number; length: number; radius: number; ticks: number; dmg: number }): void => {
  if (e.timer < b.every) e.timer++;
  if (e.timer < b.every) return;
  let best: Hero | null = null;
  let bd = b.range * b.range + 1;
  for (const h of s.heroes) {
    // A hidden shot isn't seen to block.
    if (h.downTicks > 0 || (h.hidden ?? 0) > 0) continue;
    const dd = dist2(e.x, e.y, h.x, h.y);
    if (dd < bd) {
      bd = dd;
      best = h;
    }
  }
  if (!best) return;
  e.timer = 0;
  e.anim = 'cast';
  e.animT = 0;
  e.facing = iatan2(best.y - e.y, best.x - e.x);
  // Along the line from the wyrmling through the hero, centred on the hero: fires a radius and a half apart.
  const dx = best.x - e.x;
  const dy = best.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const step = b.radius + idiv(b.radius, 2);
  const n = idiv(b.length, step);
  const half = idiv(n * step, 2);
  for (let i = 0; i <= n; i++) {
    const k = i * step - half;
    const x = best.x + idiv(dx * k, d);
    const y = best.y + idiv(dy * k, d);
    s.zones.push({ id: s.nextId++, kind: 'wyrmfire', x, y, radius: b.radius, cells: [], ticks: b.ticks, every: 12, damage: b.dmg, slowBp: 0, pid: '' });
  }
  ev(s, { t: 'shot', from: e.id, to: best.id, kind: 'wyrmBreath', x: best.x - idiv(dx * half, d), y: best.y - idiv(dy * half, d), tx: best.x + idiv(dx * half, d), ty: best.y + idiv(dy * half, d) });
};

/** The slinger: a hero within its throw, and it stands and slings instead of walking on. */
const slingTick = (s: BattleState, e: Enemy, range: number): boolean => {
  let best: Hero | null = null;
  let bd = range * range + 1;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const dd = dist2(e.x, e.y, h.x, h.y);
    if (dd < bd) {
      bd = dd;
      best = h;
    }
  }
  if (!best) return false;
  e.facing = iatan2(best.y - e.y, best.x - e.x);
  if (e.cd === 0) {
    ev(s, { t: 'shot', from: e.id, to: best.id, kind: 'sling', x: e.x, y: e.y, tx: best.x, ty: best.y });
    attackHero(s, e, best);
  }
  return true;
};

const attackStructure = (s: BattleState, e: Enemy, st: Structure): void => {
  const c = hexToWorld(st.q, st.r);
  e.facing = iatan2(c.y - e.y, c.x - e.x);
  if (e.cd > 0) return;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  const wall = NIGHTMARES[e.kind].wallBp;
  const acid = wall && (st.type === 'wall' || st.type === 'gate' || st.type === 'rampart') ? mulBp(e.damage, wall) : e.damage;
  // Bruni's hollow forge: his blows on any structure land harder.
  const forge = NIGHTMARES[e.kind].structureBp;
  damageStructure(s, st, blowOf(s, e, forge ? mulBp(acid, forge) : acid), e);
  // A frost colossus's blow leaves the stone brittle.
  const br = NIGHTMARES[e.kind].brittles;
  if (br && !st.destroyed) st.brittleUntil = s.tick + br.ticks;
};

/** A blow at a bot (an automaton, the caravan). */
const attackBot = (s: BattleState, e: Enemy, b: Bot): void => {
  e.facing = iatan2(b.y - e.y, b.x - e.x);
  if (e.cd > 0) return;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  b.hp -= e.damage;
  ev(s, { t: 'hit', id: b.id, dmg: e.damage, x: b.x, y: b.y });
  // A decoy with spikes hurts whoever strikes it.
  if (b.reflectBp) damageEnemy(s, e, mulBp(e.damage, b.reflectBp), { pid: b.pid });
};

/** The saga's caravan while it stands (escort acts). */
const escortCart = (s: BattleState): Bot | undefined => (s.escort ? s.bots?.find((b) => b.id === s.escort?.cart && b.hp > 0) : undefined);

const attackKeep = (s: BattleState, e: Enemy): void => {
  e.facing = iatan2(-e.y, -e.x);
  if (e.cd > 0) return;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  damageKeep(s, blowOf(s, e, e.damage), e);
};

/** A hero standing in the way blocks us (up to its capacity), unless the place calls us past (rush). A siege ram doesn't stop for heroes. */
const blockedByHero = (s: BattleState, e: Enemy, engaged: Map<number, number>): boolean => {
  const d = NIGHTMARES[e.kind];
  if (d.structureHunter || d.siege || e.kind === 'shell' || s.rules?.rush) return false;
  const r = d.reach + 200;
  let best: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const dd = dist2(e.x, e.y, h.x, h.y);
    if (dd > r * r) continue;
    if ((engaged.get(h.id) ?? 0) >= h.stats.blockCapacity) continue;
    if (dd < bd) {
      bd = dd;
      best = h;
    }
  }
  if (!best) return false;
  e.tgtKind = 1;
  e.tgtId = best.id;
  engaged.set(best.id, (engaged.get(best.id) ?? 0) + 1);
  attackHero(s, e, best);
  return true;
};

const groundTick = (s: BattleState, e: Enemy, engaged: Map<number, number>): void => {
  const d = NIGHTMARES[e.kind];
  const reach = d.reach;
  // 0) A mammoth that fire panicked flees outward, over whatever walks beside it.
  if (d.spooks && (e.panic ?? 0) > s.tick) {
    panicTick(s, e, d.spooks);
    return;
  }
  // 1) Taunt overrides everything.
  if (e.taunter > 0) {
    const h = findHero(s, e.taunter);
    if (heroAlive(h)) {
      if (e.tgtKind !== 1 || e.tgtId !== h.id) {
        if (e.tgtKind === 1) engaged.set(e.tgtId, (engaged.get(e.tgtId) ?? 1) - 1);
        e.tgtKind = 1;
        e.tgtId = h.id;
        engaged.set(h.id, (engaged.get(h.id) ?? 0) + 1);
      }
      if (withinRange(e.x, e.y, h.x, h.y, reach + 250)) attackHero(s, e, h);
      else {
        e.anim = 'walk';
        chaseStep(s, e, h.x, h.y, movePx(e, s.tick));
      }
      return;
    }
    e.taunter = 0;
  }
  // 2) Keep fighting the hero we are engaged with while it stays close.
  if (e.tgtKind === 1) {
    const h = findHero(s, e.tgtId);
    if (heroAlive(h) && !((h?.hidden ?? 0) > 0) && withinRange(e.x, e.y, h.x, h.y, reach + 450)) {
      if (withinRange(e.x, e.y, h.x, h.y, reach + 150)) attackHero(s, e, h);
      else chaseStep(s, e, h.x, h.y, movePx(e, s.tick));
      return;
    }
    engaged.set(e.tgtId, (engaged.get(e.tgtId) ?? 1) - 1);
    e.tgtKind = 0;
    e.tgtId = 0;
  }
  // 3) An artificer's automaton draws us in, and we try to break it.
  // The saga's caravan draws them too, but a hero in the way still holds them first.
  if (!d.structureHunter && e.kind !== 'shell') {
    const b = drawnBy(s, e);
    if (b?.kind === 'cart' && blockedByHero(s, e, engaged)) return;
    if (b) {
      if (withinRange(e.x, e.y, b.x, b.y, reach + 400)) {
        e.facing = iatan2(b.y - e.y, b.x - e.x);
        e.tgtKind = 0;
        attackBot(s, e, b);
      } else if (b.kind === 'cart' && !withinRange(e.x, e.y, b.x, b.y, s.rules?.escort?.draw ?? 0)) {
        // Far from the caravan: along the ground to it (straight across, the glass would stop it).
        walkFlow(s, e);
      } else {
        e.anim = 'walk';
        chaseStep(s, e, b.x, b.y, movePx(e, s.tick));
      }
      return;
    }
  }
  // 4) A hero standing in the way blocks us (up to its capacity).
  if (blockedByHero(s, e, engaged)) return;
  // 5) A portable turret within reach gets smashed on the way (incursions, newer rules; the rush ignores them).
  if (!s.rules?.rush) {
    for (const t of s.turrets) {
      if (!t.portable || (t.hp ?? 0) <= 0 || !withinRange(e.x, e.y, t.x, t.y, reach + 300)) continue;
      e.facing = iatan2(t.y - e.y, t.x - e.x);
      e.tgtKind = 0;
      if (e.cd === 0) {
        e.cd = e.reload;
        e.anim = 'attack';
        e.animT = 0;
        t.hp = (t.hp ?? 0) - e.damage;
        ev(s, { t: 'hit', id: t.id, dmg: e.damage, x: t.x, y: t.y });
      }
      return;
    }
  }
  // 6) A mangonel outside the walls stops to lob rocks: at the walls first, then the towers.
  if (d.lobs && !s.wave.lastStand && !(e.hushUntil && hushed(s, e)) && lobTick(s, e, d.lobs)) return;
  // 7) A spectral archer outside the walls shoots the nearest tower it can reach instead of coming in.
  if (d.shootsTowers && !s.wave.lastStand && !(e.hushUntil && hushed(s, e))) {
    const cell = cellHex(s.map.radius, enemyCell(s, e));
    if (!isInside(s.map, cell.q, cell.r)) {
      const t = nearestTowerWithin(s, e, d.shootsTowers);
      if (t) {
        e.tgtKind = 2;
        e.tgtId = t.id;
        if (e.cd === 0) {
          const c = hexToWorld(t.q, t.r);
          ev(s, { t: 'shot', from: e.id, to: t.id, kind: 'spectral', x: e.x, y: e.y, tx: c.x, ty: c.y });
        }
        attackStructure(s, e, t);
        return;
      }
    }
  }
  // 8) A battle plan's squad waiting out of reach, or keeping by its engine (battle/plan.ts).
  if (e.squad !== undefined && s.plan && squadTick(s, e)) return;
  // 9) Walk the flow field toward the keep, breaking whatever blocks the way (a flanking one heads for its spot first).
  if (e.spot !== undefined) steerToSpot(s, e);
  else if (e.rally !== undefined) steerToRally(s, e);
  walkFlow(s, e);
};

/** A panicked mammoth: away from the keep, trampling the nightmares around it every second. */
const panicTick = (s: BattleState, e: Enemy, sp: { ticks: number; radius: number; dmg: number }): void => {
  e.anim = 'walk';
  e.tgtKind = 0;
  e.next = -1;
  const len = imax(1, isqrt(e.x * e.x + e.y * e.y));
  chaseStep(s, e, e.x + idiv(e.x * 3000, len), e.y + idiv(e.y * 3000, len), movePx(e, s.tick));
  if ((s.tick - ((e.panic ?? 0) - sp.ticks)) % 12 !== 0) return;
  for (const o of s.enemies) {
    if (o.id === e.id || o.hp <= 0 || isFlying(o) || !withinRange(e.x, e.y, o.x, o.y, sp.radius)) continue;
    damageEnemy(s, o, sp.dmg, { pid: '', ignoresArmor: true });
  }
};

/**
 * Where a waiting nightmare stands at its staging cell: a spot of its own around the cell, so the bulk
 * gathers as a crowd rather than a pile.
 */
const holdSpot = (s: BattleState, e: Enemy, stage: number): { x: number; y: number } => {
  const c = cellCenter(s.map.radius, stage);
  return { x: c.x + ((e.id * 37) % 9 - 4) * 160, y: c.y + ((e.id * 53) % 9 - 4) * 160 };
};

/** Waits at the staging cell: walks (or flies) there, then stands facing the bastion. */
const holdAt = (s: BattleState, e: Enemy, stage: number, fly: boolean): void => {
  const p = holdSpot(s, e, stage);
  if (!withinRange(e.x, e.y, p.x, p.y, 250)) {
    e.anim = 'walk';
    if (fly) stepToward(e, p.x, p.y, movePx(e, s.tick));
    else chaseStep(s, e, p.x, p.y, movePx(e, s.tick));
    return;
  }
  e.anim = 'idle';
  e.facing = iatan2(-e.y, -e.x);
};

/**
 * A ground nightmare's squad order, when it isn't simply walking in: waiting at its staging cell, or
 * guarding its engine (it goes after a hero who comes near the engine, else keeps on the engine's bastion
 * side). Returns whether the order moved it this tick.
 */
const squadTick = (s: BattleState, e: Enemy): boolean => {
  const q = s.plan?.squads[e.squad as number];
  if (!q) return false;
  if (q.order === 'hold') {
    if (q.stage < 0) return false;
    // Shifted: round the bastion first, waypoint by waypoint.
    const leg = e.leg ?? 0;
    const way = q.way[leg];
    if (way !== undefined) {
      const c = cellCenter(s.map.radius, way);
      if (withinRange(e.x, e.y, c.x, c.y, PLAN.wayReach)) e.leg = leg + 1;
      else {
        e.anim = 'walk';
        chaseStep(s, e, c.x, c.y, movePx(e, s.tick));
        return true;
      }
    }
    holdAt(s, e, q.stage, false);
    return true;
  }
  if (q.order !== 'escort') return false;
  const eng = engineFor(s, e);
  if (!eng) return false;
  let foe: Hero | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (!heroAlive(h) || !withinRange(h.x, h.y, eng.x, eng.y, PLAN.guardRadius)) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      foe = h;
    }
  }
  if (foe) {
    e.tgtKind = 1;
    e.tgtId = foe.id;
    if (withinRange(e.x, e.y, foe.x, foe.y, NIGHTMARES[e.kind].reach + 150)) attackHero(s, e, foe);
    else {
      e.anim = 'walk';
      chaseStep(s, e, foe.x, foe.y, movePx(e, s.tick));
    }
    return true;
  }
  // On the engine's bastion side, spread a little by id.
  const len = imax(1, isqrt(eng.x * eng.x + eng.y * eng.y));
  const side = ((e.id * 37) % 7) - 3;
  const tx = eng.x - idiv(eng.x * PLAN.guardLeash, len) - idiv(eng.y * side * 220, len);
  const ty = eng.y - idiv(eng.y * PLAN.guardLeash, len) + idiv(eng.x * side * 220, len);
  if (!withinRange(e.x, e.y, tx, ty, 300)) {
    e.anim = 'walk';
    chaseStep(s, e, tx, ty, movePx(e, s.tick));
  } else {
    e.anim = 'idle';
    e.facing = iatan2(-e.y, -e.x);
  }
  return true;
};

/** Whether a ground nightmare could stand on this cell (the flow field reaches it). */
const ground = (s: BattleState, id: number): boolean => id >= 0 && cache(s).flow[id] !== -1;

/**
 * A ground nightmare going straight for a hero or a bot, but only over ground it could walk: off an
 * incursion's paths lies glass no hero can stand on, and a nightmare out there (the colossus, with its
 * long reach) could hit them while nobody could hit it back. Blocked, it steps to the neighbouring
 * ground cell that brings it closest; none does, it waits.
 */
export const chaseStep = (s: BattleState, e: Enemy, tx: number, ty: number, px: number): void => {
  const cur = enemyCell(s, e);
  const [x, y, facing] = [e.x, e.y, e.facing];
  stepToward(e, tx, ty, px);
  const now = enemyCell(s, e);
  // A standing structure is on the flow field (the walk breaks through it), but never ground to step onto.
  const block = now === cur ? null : blockingAtCell(s, now);
  if (now === cur || (ground(s, now) && !block)) return;
  e.x = x;
  e.y = y;
  e.facing = facing;
  // A wall, gate or tower between it and what it chases: it breaks its way through, as on its walk. Walking
  // on let nightmares after a hero behind the wall cross it at full health.
  if (block && block.hp > 0) {
    attackStructure(s, e, block);
    return;
  }
  if (cur < 0) return;
  const R = s.map.radius;
  const here = cellHex(R, cur);
  let best = -1;
  let bd = dist2(x, y, tx, ty);
  for (const d of DIRS) {
    const n = cellId(R, here.q + d.q, here.r + d.r);
    if (!ground(s, n) || blockingAtCell(s, n)) continue;
    const c = cellCenter(R, n);
    const nd = dist2(c.x, c.y, tx, ty);
    if (nd < bd || (nd === bd && n < best)) {
      bd = nd;
      best = n;
    }
  }
  if (best < 0) return;
  const c = cellCenter(R, best);
  stepToward(e, c.x, c.y, px);
};

/** Out on ground no nightmare walks (pushed or strayed there): the nearest cell that is ground, within three steps. */
const backToGround = (s: BattleState, cur: number): number => {
  const R = s.map.radius;
  const here = cellHex(R, cur);
  for (let k = 1; k <= 3; k++) {
    let best = -1;
    for (let dq = -k; dq <= k; dq++) {
      for (let dr = -k; dr <= k; dr++) {
        if (hexLen(dq, dr) !== k) continue;
        const n = cellId(R, here.q + dq, here.r + dr);
        if (ground(s, n) && (best < 0 || n < best)) best = n;
      }
    }
    if (best >= 0) return best;
  }
  return cur;
};

/**
 * Sent at a wall spot (a live general's strike or pulse): it follows the spot's own way outside the wall; two
 * steps off, the rally takes over (and sets on the wall in front). With no way there, the rally alone.
 */
const steerToSpot = (s: BattleState, e: Enemy): void => {
  const spot = e.spot as number;
  const f = spotFlow(s, spot);
  const cur = enemyCell(s, e);
  const here = cur < 0 ? -1 : (f[cur] as number);
  if (here < 0 || here <= 2 * SPOT_STEP) {
    e.spot = undefined;
    e.rally = spot;
    steerToRally(s, e);
    return;
  }
  if (e.next >= 0 && e.next !== cur && (f[e.next] as number) >= 0 && (f[e.next] as number) < here) return;
  const R = s.map.radius;
  const h = cellHex(R, cur);
  let best = -1;
  let bd = here;
  for (const d of DIRS) {
    const n = cellId(R, h.q + d.q, h.r + d.r);
    const v = n < 0 ? -1 : (f[n] as number);
    if (v >= 0 && (v < bd || (v === bd && n < best))) {
      bd = v;
      best = n;
    }
  }
  if (best >= 0) e.next = best;
};
/** A step's cost in the flow fields (grid.ts). */
const SPOT_STEP = 10;

/**
 * A flanking nightmare: near the wall it leaves the path for its rally cell outside the wall, one step
 * at a time over open ground, and once there sets on the wall in front. It gives up (back to the flow)
 * if it can't get any closer, or once the wall there is down.
 */
const steerToRally = (s: BattleState, e: Enemy): void => {
  const R = s.map.radius;
  const rally = e.rally as number;
  const cur = enemyCell(s, e);
  const here = cellHex(R, cur);
  if (hexLen(here.q, here.r) > s.map.bastion + WAVE.flankFrom) return;
  const goal = cellHex(R, rally);
  if (cur === rally) {
    e.rally = undefined;
    // The wall in front: the neighbour one step in with a standing rampart or wall (lowest id on a tie).
    for (const d of DIRS) {
      const n = cellId(R, goal.q + d.q, goal.r + d.r);
      if (n < 0 || hexDist(cellHex(R, n), { q: 0, r: 0 }) !== s.map.bastion) continue;
      const b = blockingAtCell(s, n);
      if (b && b.hp > 0 && (b.type === 'rampart' || b.type === 'wall')) {
        e.next = n;
        return;
      }
    }
    return;
  }
  if (e.next >= 0 && e.next !== cur && hexDist(cellHex(R, e.next), goal) < hexDist(here, goal)) return;
  const c = cache(s);
  let best = -1;
  let bd = hexDist(here, goal);
  for (const d of DIRS) {
    const n = cellId(R, here.q + d.q, here.r + d.r);
    if (n < 0 || c.flow[n] === -1 || blockingAtCell(s, n)) continue;
    const nd = hexDist(cellHex(R, n), goal);
    if (nd < bd) {
      bd = nd;
      best = n;
    }
  }
  if (best < 0) e.rally = undefined;
  else e.next = best;
};

const walkFlow = (s: BattleState, e: Enemy): void => {
  const R = s.map.radius;
  const c = cache(s);
  const cur = enemyCell(s, e);
  // A mammoth keeps its own straight way in.
  const tramples = !!NIGHTMARES[e.kind].tramples;
  // On terraced land each kind weighs walls its own way (docs/CONSTRUCCION.md).
  const cls = s.map.land ? (WALL_CLASS[e.kind as NightmareType] ?? 'normal') : 'normal';
  const field = tramples ? trampleFlow(s) : classFlow(s, cls);
  if (e.next < 0 || e.next === cur || field[e.next] === -1) {
    e.next = flowNext(s, cur, tramples, cls);
    if (e.next < 0) {
      // Off the field (e.g. knocked out of a cell): head back to the closest walkable ground.
      e.next = cur < 0 || ground(s, cur) ? cur : backToGround(s, cur);
    }
  }
  const nt = c.terrain[e.next] as number;
  // (An escort has no node: the flow leads to the caravan instead.)
  if (!s.escort && (nt === T_KEEP || cur === c.keepCell)) {
    if (withinRange(e.x, e.y, 0, 0, NIGHTMARES[e.kind].reach + KEEP_REACH)) {
      e.tgtKind = 3;
      if (!NIGHTMARES[e.kind].avoidsKeep) attackKeep(s, e);
      return;
    }
  }
  const block = blockingAtCell(s, e.next);
  if (block && block.hp > 0) {
    // A three-cell building is struck at the cell in the way, not at its own.
    const bc = block.foot ? cellCenter(R, e.next) : hexToWorld(block.q, block.r);
    if (withinRange(e.x, e.y, bc.x, bc.y, NIGHTMARES[e.kind].reach + STRUCT_REACH)) {
      // A sapper digs under a wall, gate or rampart instead of breaking it, once.
      const dig = NIGHTMARES[e.kind].burrows;
      if (dig && e.phase === 0 && (block.type === 'rampart' || block.type === 'wall' || block.type === 'gate')) {
        const into = beyond(s, e, block);
        if (into) {
          e.phase = 1;
          e.timer = dig;
          e.digX = into.x;
          e.digY = into.y;
          e.anim = 'burrow';
          ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'burrow' });
          return;
        }
      }
      e.tgtKind = 2;
      e.tgtId = block.id;
      attackStructure(s, e, block);
      return;
    }
  }
  const bone = boneWallAt(s, e.next);
  if (bone) {
    // Bone walls of the Hollow Shell are obstacles the flow field routes around; wait if stuck on one.
    e.next = -1;
    return;
  }
  e.tgtKind = 0;
  e.anim = 'walk';
  const from = cellCenter(R, cur);
  const to = cellCenter(R, e.next);
  // Lateral lane offset perpendicular to the step direction (cell centers are 1000 apart).
  const lx = idiv(-(to.y - from.y) * e.lane, 1000);
  const ly = idiv((to.x - from.x) * e.lane, 1000);
  const px = movePx(e, s.tick);
  const tx = nt === T_KEEP ? 0 : to.x + lx;
  const ty = nt === T_KEEP ? 0 : to.y + ly;
  stepToward(e, tx, ty, px);
  const now = enemyCell(s, e);
  if (now === e.next) e.next = -1;
  if (now !== e.trapCell) {
    e.trapCell = now;
    triggerTrap(s, e, now);
  }
};

/**
 * The bone walls among the nightmares, so walkers don't scan the whole list every step. The list
 * only grows by push and is replaced when the dead are reaped, so its identity and length tell
 * whether this is still current. Each wall keeps the cell it was last seen on, redone if it moved.
 */
interface BoneSeen {
  e: Enemy;
  x: number;
  y: number;
  cell: number;
}
const BONE = new WeakMap<Enemy[], { n: number; walls: BoneSeen[] }>();
const boneWalls = (s: BattleState): BoneSeen[] => {
  const m = BONE.get(s.enemies);
  if (m && m.n === s.enemies.length) return m.walls;
  const walls = s.enemies.filter((o) => o.kind === 'boneWall').map((e) => ({ e, x: e.x, y: e.y, cell: enemyCell(s, e) }));
  BONE.set(s.enemies, { n: s.enemies.length, walls });
  return walls;
};

const boneWallAt = (s: BattleState, cell: number): Enemy | null => {
  for (const w of boneWalls(s)) {
    const o = w.e;
    if (o.hp <= 0) continue;
    if (o.x !== w.x || o.y !== w.y) {
      w.x = o.x;
      w.y = o.y;
      w.cell = cellIdAt(s, o.x, o.y);
    }
    if (w.cell === cell) return o;
  }
  return null;
};

const triggerTrap = (s: BattleState, e: Enemy, cell: number): void => {
  if (NIGHTMARES[e.kind].ignoresTraps || isFlying(e)) return;
  const trap = trapAtCell(s, cell);
  if (!trap || trap.charges <= 0) return;
  const def = STRUCTURES[trap.type];
  const dmg = mulBp(def.damage[trap.level - 1] as number, s.rules?.builtBp ?? 10000);
  trap.charges--;
  ev(s, { t: 'shot', from: trap.id, to: e.id, kind: trap.type, x: e.x, y: e.y, tx: e.x, ty: e.y });
  if (trap.type === 'spikes') {
    damageEnemy(s, e, dmg, { pid: '' });
    e.slowBp = def.slowBp ?? 0;
    e.slowTicks = def.slowTicks ?? 0;
  } else if (trap.type === 'oil') {
    e.dotDmg = dmg;
    e.dotType = undefined;
    e.dotEvery = def.dotEvery ?? 12;
    e.dotTicks = def.dotTicks ?? 60;
  }
  if (trap.charges === 0) ev(s, { t: 'struct', id: trap.id, what: 'destroyed' });
};

const flyerTick = (s: BattleState, e: Enemy): void => {
  const d = NIGHTMARES[e.kind];
  // A challenge pulls flyers down too: they come at the warrior, within reach of his blows.
  if (e.taunter > 0) {
    const h = findHero(s, e.taunter);
    if (heroAlive(h)) {
      e.tgtKind = 1;
      e.tgtId = h.id;
      if (withinRange(e.x, e.y, h.x, h.y, d.reach + 250)) attackHero(s, e, h);
      else {
        e.anim = 'walk';
        stepToward(e, h.x, h.y, movePx(e, s.tick));
      }
      return;
    }
    e.taunter = 0;
  }
  // A plan's flyers wait over the staging cell until the general sends them.
  if (e.squad !== undefined && s.plan) {
    const q = s.plan.squads[e.squad];
    if (q && q.order === 'hold' && q.stage >= 0) {
      holdAt(s, e, q.stage, true);
      return;
    }
  }
  // Wyvern: it hovers off a tower, out of a hero's reach, and spits fire that keeps the tower burning.
  // Storm herald: the same hovering, and lightning that leaps from building to building.
  // Glass drake: the same hovering, and a cone of fire swept over every building before it.
  if (d.sweeps) {
    let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
    if (!st || st.destroyed) {
      st = nearestStructure(s, e, true, e.id % 3) ?? nearestStructure(s, e, false);
      e.tgtKind = st ? 2 : 0;
      e.tgtId = st ? st.id : 0;
    }
    if (st) {
      const c = hexToWorld(st.q, st.r);
      if (withinRange(e.x, e.y, c.x, c.y, d.sweeps.range)) {
        e.facing = iatan2(c.y - e.y, c.x - e.x);
        if (e.cd === 0) {
          e.cd = e.reload;
          e.anim = 'cast';
          e.animT = 0;
          drakeSweep(s, e, d.sweeps);
        }
      } else {
        e.anim = 'walk';
        stepToward(e, c.x, c.y, movePx(e, s.tick));
      }
      return;
    }
  }
  if (d.calls) {
    let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
    if (!st || st.destroyed) {
      st = nearestStructure(s, e, true, e.id % 3) ?? nearestStructure(s, e, false);
      e.tgtKind = st ? 2 : 0;
      e.tgtId = st ? st.id : 0;
    }
    if (st) {
      const c = hexToWorld(st.q, st.r);
      if (withinRange(e.x, e.y, c.x, c.y, d.calls.range)) {
        e.facing = iatan2(c.y - e.y, c.x - e.x);
        if (e.cd === 0) {
          e.cd = e.reload;
          e.anim = 'cast';
          e.animT = 0;
          stormBolt(s, e, st, d.calls);
        }
      } else {
        e.anim = 'walk';
        stepToward(e, c.x, c.y, movePx(e, s.tick));
      }
      return;
    }
  }
  if (d.spitsFire) {
    let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
    if (!st || st.destroyed) {
      st = nearestStructure(s, e, true, e.id % 3) ?? nearestStructure(s, e, false);
      e.tgtKind = st ? 2 : 0;
      e.tgtId = st ? st.id : 0;
    }
    if (st) {
      const c = hexToWorld(st.q, st.r);
      if (withinRange(e.x, e.y, c.x, c.y, d.spitsFire.range)) {
        e.facing = iatan2(c.y - e.y, c.x - e.x);
        if (e.cd === 0) {
          e.cd = e.reload;
          e.anim = 'attack';
          e.animT = 0;
          ev(s, { t: 'shot', from: e.id, to: st.id, kind: 'wyvernFire', x: e.x, y: e.y, tx: c.x, ty: c.y });
          damageStructure(s, st, blowOf(s, e, e.damage), e);
          if (!st.destroyed) {
            st.burnTicks = imax(st.burnTicks ?? 0, d.spitsFire.burn.ticks);
            st.burnDmg = d.spitsFire.burn.dmg;
          }
        }
      } else {
        e.anim = 'walk';
        stepToward(e, c.x, c.y, movePx(e, s.tick));
      }
      return;
    }
  }
  if (d.structureHunter) {
    // Gargoyle: hunt the nearest tower, never the keep.
    let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
    if (!st || st.destroyed) {
      // Each gargoyle takes one of the three nearest towers (by its id), so a flock spreads instead of piling on one.
      st = nearestStructure(s, e, true, e.id % 3) ?? nearestStructure(s, e, false);
      e.tgtKind = st ? 2 : 0;
      e.tgtId = st ? st.id : 0;
    }
    if (st) {
      const c = hexToWorld(st.q, st.r);
      if (withinRange(e.x, e.y, c.x, c.y, d.reach + STRUCT_REACH)) attackStructure(s, e, st);
      else stepToward(e, c.x, c.y, movePx(e, s.tick));
      return;
    }
    // Nothing left to break: harass the nearest hero.
    let best: Hero | null = null;
    let bd = 0x7fffffff;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      const dd = dist2(e.x, e.y, h.x, h.y);
      if (dd < bd) {
        bd = dd;
        best = h;
      }
    }
    if (best) {
      if (withinRange(e.x, e.y, best.x, best.y, d.reach + 150)) attackHero(s, e, best);
      else stepToward(e, best.x, best.y, movePx(e, s.tick));
    }
    return;
  }
  // Crows: the nearest hero close enough, pecked; none, they go on for the keep like a wraith.
  if (d.huntsHeroes) {
    let best: Hero | null = null;
    let bd = d.huntsHeroes * d.huntsHeroes + 1;
    // An ash watcher: of the heroes in reach, the one with the least health left.
    let weakest = 10001;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      const dd = dist2(e.x, e.y, h.x, h.y);
      if (d.preysOnWeak) {
        if (dd > d.huntsHeroes * d.huntsHeroes) continue;
        const left = idiv(h.hp * 10000, imax(1, h.stats.maxHp));
        if (left < weakest) {
          weakest = left;
          best = h;
        }
      } else if (dd < bd) {
        bd = dd;
        best = h;
      }
    }
    if (best) {
      e.tgtKind = 1;
      e.tgtId = best.id;
      if (withinRange(e.x, e.y, best.x, best.y, d.reach + 150)) attackHero(s, e, best);
      else {
        e.anim = 'walk';
        stepToward(e, best.x, best.y, movePx(e, s.tick));
      }
      return;
    }
  }
  // An escort has no node: what would fly to it goes for the caravan.
  if (s.escort) {
    const cart = escortCart(s);
    if (!cart) return;
    e.tgtKind = 0;
    if (withinRange(e.x, e.y, cart.x, cart.y, d.reach + 400)) attackBot(s, e, cart);
    else {
      e.anim = 'walk';
      stepToward(e, cart.x, cart.y, movePx(e, s.tick));
    }
    return;
  }
  // Wraith: straight over the walls to the keep.
  if (withinRange(e.x, e.y, 0, 0, d.reach + KEEP_REACH)) {
    e.tgtKind = 3;
    attackKeep(s, e);
    return;
  }
  e.anim = 'walk';
  stepToward(e, e.lane, -e.lane, movePx(e, s.tick));
};

/** The nearest standing structure (towers only, if asked); `rank` picks the second or third nearest instead, when there are that many. */
/** The glass drake's sweep: every building in the cone before it takes the blow and catches fire. */
const drakeSweep = (s: BattleState, e: Enemy, w: { range: number; arc: number; burnTicks: number; burnDmg: number }): void => {
  ev(s, { t: 'shot', from: e.id, to: 0, kind: 'drakeSweep', x: e.x, y: e.y, tx: e.x + idiv(cosA(e.facing) * w.range, 10000), ty: e.y + idiv(sinA(e.facing) * w.range, 10000) });
  const dmg = blowOf(s, e, e.damage);
  for (const st of s.structures) {
    if (st.destroyed || st.maxHp <= 0) continue;
    const c = hexToWorld(st.q, st.r);
    if (!withinRange(e.x, e.y, c.x, c.y, w.range)) continue;
    const d = (((iatan2(c.y - e.y, c.x - e.x) - e.facing) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS - ANGLE_STEPS / 2;
    if (d > w.arc || d < -w.arc) continue;
    damageStructure(s, st, dmg, e, 'fire');
    if (!st.destroyed) {
      st.burnTicks = imax(st.burnTicks ?? 0, w.burnTicks);
      st.burnDmg = w.burnDmg;
    }
  }
};

/** The shellback: as it breaks past each mark, the nightmares clinging to its back drop off around it. */
const carryTick = (s: BattleState, e: Enemy, c: { at: number[]; count: number; kinds: NightmareType[] }): void => {
  let due = 0;
  for (const bp of c.at) if (e.hp * 10000 <= e.maxHp * bp) due++;
  while ((e.dropped ?? 0) < due) {
    e.dropped = (e.dropped ?? 0) + 1;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 1500, kind: 'shake' });
    for (let i = 0; i < c.count; i++) {
      const a = idiv(i * ANGLE_STEPS, c.count) + (e.dropped ?? 0) * 97;
      const kind = c.kinds[(i + (e.dropped ?? 0)) % c.kinds.length] as NightmareType;
      spawnEnemy(s, kind, e.tier === 3 ? 2 : e.tier, false, e.sector, e.x + idiv(cosA(a) * 900, 10000), e.y + idiv(sinA(a) * 900, 10000));
    }
  }
};

/** The lidless dreamer: every so often its eyes open, and the towers and heroes around it fall asleep. */
const dreamTick = (s: BattleState, e: Enemy, d: { every: number; radius: number; ticks: number }): void => {
  if ((s.tick - e.born) % d.every !== d.every - 1) return;
  e.anim = 'cast';
  e.animT = 0;
  e.openUntil = s.tick + d.ticks;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: d.radius, kind: 'dream' });
  for (const st of s.structures) {
    if (st.destroyed || !firesAsTower(st)) continue;
    const c = hexToWorld(st.q, st.r);
    if (withinRange(e.x, e.y, c.x, c.y, d.radius)) st.dozeUntil = s.tick + d.ticks;
  }
  for (const h of s.heroes) {
    if (h.downTicks > 0 || (h.frenzy ?? 0) > 0 || !withinRange(e.x, e.y, h.x, h.y, d.radius)) continue;
    h.sleep = imax(h.sleep ?? 0, d.ticks);
  }
};

/** The storm herald's bolt: it strikes a building, then leaps to the nearest ones not yet struck, weaker each time. */
/** The lightning rods among the structures, once per fight (the list doesn't change while it lasts; a fallen one is skipped). */
const RODS = new WeakMap<Structure[], Structure[]>();
const rods = (s: BattleState): Structure[] => {
  let list = RODS.get(s.structures);
  if (!list) {
    list = s.structures.filter((o) => o.type === 'rod');
    RODS.set(s.structures, list);
  }
  return list;
};

const stormBolt = (s: BattleState, e: Enemy, first: Structure, c: { chain: number; chainRange: number; chainBp: number }): void => {
  const struck = new Set<number>();
  let at = first;
  let dmg = blowOf(s, e, e.damage);
  let from = { x: e.x, y: e.y };
  for (let i = 0; i <= c.chain; i++) {
    const p = hexToWorld(at.q, at.r);
    ev(s, { t: 'shot', from: i === 0 ? e.id : 0, to: at.id, kind: i === 0 ? 'stormBolt' : 'stormChain', x: from.x, y: from.y, tx: p.x, ty: p.y });
    // A lightning rod takes the bolt into the ground: it ends there, unharmed (terraced land, era IV).
    if (at.type === 'rod') break;
    damageStructure(s, at, dmg, e, 'lightning');
    struck.add(at.id);
    from = p;
    dmg = mulBp(dmg, c.chainBp);
    if (dmg < 1) break;
    let next: Structure | null = null;
    // A standing rod within its reach of where the bolt is draws the next leap to it.
    for (const o of rods(s)) {
      if (o.destroyed) continue;
      const q = hexToWorld(o.q, o.r);
      if (dist2(p.x, p.y, q.x, q.y) <= STRUCTURES.rod.range * STRUCTURES.rod.range) {
        next = o;
        break;
      }
    }
    if (next) {
      at = next;
      continue;
    }
    let bd = c.chainRange * c.chainRange + 1;
    for (const o of s.structures) {
      if (o.destroyed || o.maxHp <= 0 || struck.has(o.id)) continue;
      const q = hexToWorld(o.q, o.r);
      const dd = dist2(p.x, p.y, q.x, q.y);
      if (dd < bd) {
        bd = dd;
        next = o;
      }
    }
    if (!next) break;
    at = next;
  }
};

/** The frost colossus's breath: frost in a cone toward the nearest hero in reach; the heroes in it freeze. */
const freezeTick = (s: BattleState, e: Enemy, f: { every: number; range: number; arc: number; ticks: number; dmg: number }): void => {
  if ((s.tick - e.born) % f.every !== f.every - 1) return;
  let aim: Hero | null = null;
  let bd = f.range * f.range + 1;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const dd = dist2(e.x, e.y, h.x, h.y);
    if (dd < bd) {
      bd = dd;
      aim = h;
    }
  }
  if (!aim) return;
  const dir = iatan2(aim.y - e.y, aim.x - e.x);
  e.facing = dir;
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'shot', from: e.id, to: aim.id, kind: 'frostBreath', x: e.x, y: e.y, tx: aim.x, ty: aim.y });
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !withinRange(e.x, e.y, h.x, h.y, f.range)) continue;
    const d = (((iatan2(h.y - e.y, h.x - e.x) - dir) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS - ANGLE_STEPS / 2;
    if (d > f.arc || d < -f.arc) continue;
    damageHero(s, h, blowOf(s, e, f.dmg), e, 'frost');
    if (h.downTicks === 0 && (h.frenzy ?? 0) <= 0) h.stun = imax(h.stun, f.ticks);
  }
};

/**
 * A shell serpent under the ground: it dives where it came in, then goes straight for a tower (past walls and
 * gates) and comes up beside it. Nothing reaches it down there.
 */
const serpentDig = (s: BattleState, e: Enemy): void => {
  const t = NIGHTMARES[e.kind].tunnels as { surface: number };
  if (e.phase !== 1) {
    e.phase = 1;
    e.anim = 'walk';
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'dive' });
  }
  let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
  if (!st || st.destroyed) {
    st = nearestStructure(s, e, true, e.id % 3);
    e.tgtKind = st ? 2 : 0;
    e.tgtId = st ? st.id : 0;
  }
  // No tower left: it comes up where it is and walks on like the rest.
  if (!st) {
    serpentSurface(s, e);
    return;
  }
  const c = hexToWorld(st.q, st.r);
  if (withinRange(e.x, e.y, c.x, c.y, t.surface)) serpentSurface(s, e);
  else stepToward(e, c.x, c.y, movePx(e, s.tick));
};

const serpentSurface = (s: BattleState, e: Enemy): void => {
  e.phase = 2;
  e.next = -1;
  e.anim = 'cast';
  e.animT = 0;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 800, kind: 'emerge' });
};

/** A surfaced shell serpent bites the nearest tower, then the next; with no tower left it walks on like the rest. */
const serpentHunt = (s: BattleState, e: Enemy): boolean => {
  let st = e.tgtKind === 2 ? findStructure(s, e.tgtId) : null;
  if (!st || st.destroyed || !firesAsTower(st)) {
    st = nearestStructure(s, e, true);
    e.tgtKind = st ? 2 : 0;
    e.tgtId = st ? st.id : 0;
  }
  if (!st) return false;
  const c = hexToWorld(st.q, st.r);
  if (withinRange(e.x, e.y, c.x, c.y, NIGHTMARES[e.kind].reach + STRUCT_REACH)) attackStructure(s, e, st);
  else {
    e.anim = 'walk';
    stepToward(e, c.x, c.y, movePx(e, s.tick));
  }
  return true;
};

const nearestStructure = (s: BattleState, e: Enemy, towersOnly: boolean, rank = 0): Structure | null => {
  const top: { st: Structure; d: number }[] = [];
  for (const st of s.structures) {
    if (st.destroyed || st.maxHp <= 0) continue;
    if (towersOnly && !firesAsTower(st)) continue;
    const c = hexToWorld(st.q, st.r);
    const dd = dist2(e.x, e.y, c.x, c.y);
    // Keep the nearest rank + 1, in order (ties by id, as structures are iterated).
    let i = top.length;
    while (i > 0 && (top[i - 1] as { d: number }).d > dd) i--;
    if (i > rank) continue;
    top.splice(i, 0, { st, d: dd });
    if (top.length > rank + 1) top.pop();
  }
  return (top[rank] ?? top[top.length - 1])?.st ?? null;
};

/** Ash Witch: curses the strongest tower nearby every 10 s; summons goblins at half HP. */
const witchTick = (s: BattleState, e: Enemy): void => {
  e.timer++;
  if (e.timer >= WITCH.curseEvery) {
    e.timer = 0;
    let best: Structure | null = null;
    for (const st of s.structures) {
      if (st.destroyed || st.cursed || !firesAsTower(st)) continue;
      const c = hexToWorld(st.q, st.r);
      if (!withinRange(e.x, e.y, c.x, c.y, WITCH.curseRange)) continue;
      if (!best || towerStrength(st) > towerStrength(best)) best = st;
    }
    if (best) {
      best.cursed = true;
      best.purify = 0;
      const c = hexToWorld(best.q, best.r);
      ev(s, { t: 'shot', from: e.id, to: best.id, kind: 'curse', x: e.x, y: e.y, tx: c.x, ty: c.y });
      ev(s, { t: 'struct', id: best.id, what: 'cursed' });
    }
  }
  if (e.phase === 0 && e.hp * 10000 <= e.maxHp * WITCH.summonAtBp) {
    e.phase = 1;
    s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', what: 'witchSummon', sector: e.sector });
    for (let i = 0; i < WITCH.summonCount; i++) {
      spawnEnemy(s, 'goblin', e.tier, false, e.sector, e.x + rngRange(s.rng, -600, 600), e.y + rngRange(s.rng, -600, 600));
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 1500, kind: 'summon' });
  }
};

const towerStrength = (st: Structure): number =>
  (STRUCTURES[st.type].damage[st.level - 1] as number) * 100 + st.level * 10 + (st.enchant ? 5 : 0);

/** Hollow Shell: spits shadows; raises bone walls at 60 %; enrages at 25 %. */
const shellTick = (s: BattleState, e: Enemy): void => {
  e.timer++;
  if (e.phase < 2 && e.hp * 10000 <= e.maxHp * SHELL.phase2AtBp) {
    e.phase = 2;
    raiseBoneWalls(s, e);
    s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', what: 'shellWalls', sector: e.sector });
  }
  if (e.phase < 3 && e.hp * 10000 <= e.maxHp * SHELL.phase3AtBp) {
    e.phase = 3;
    e.speed = mulBp(e.speed, SHELL.phase3SpeedBp);
    s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', what: 'shellEnrage', sector: e.sector });
  }
  if (e.timer >= SHELL.spitEvery) {
    e.timer = 0;
    const kind = e.phase >= 3 ? 'wraith' : 'goblin';
    for (let i = 0; i < SHELL.spitCount; i++) {
      spawnEnemy(s, kind, e.tier, false, e.sector, e.x + rngRange(s.rng, -500, 500), e.y + rngRange(s.rng, -500, 500));
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'spit' });
  }
};

const raiseBoneWalls = (s: BattleState, e: Enemy): void => {
  // Walk the flow from the shell and wall off cells a few steps ahead on the shortest path.
  const c = cache(s);
  let cell = enemyCell(s, e);
  const R = s.map.radius;
  let placed = 0;
  for (let step = 0; step < 12 && placed < SHELL.boneWalls; step++) {
    const nx = flowNext(s, cell);
    if (nx < 0 || nx === cell || c.terrain[nx] === T_KEEP) break;
    cell = nx;
    if (step < 2) continue;
    if (blockingAtCell(s, cell)) continue;
    const p = cellCenter(R, cell);
    const w = spawnEnemy(s, 'boneWall', 1, false, e.sector, p.x, p.y);
    w.lane = 0;
    placed++;
    step++;
  }
  if (placed > 0) touchGrid(s);
};

/** Bone walls behave like terrain for the flow field. */
export const boneWallCells = (s: BattleState): number[] => {
  const out: number[] = [];
  for (const o of s.enemies) if (o.kind === 'boneWall' && o.hp > 0) out.push(cellIdAt(s, o.x, o.y));
  return out;
};


/** The closest standing tower within `range` of an enemy. */
const nearestTowerWithin = (s: BattleState, e: Enemy, range: number): Structure | null => nearestWithin(s, e, range, (c) => c === 'tower');

/** Where each structure stands: they never move, so its cell's center is worked out once, not on every look. */
const centers = new WeakMap<Structure, { x: number; y: number }>();
const centerOf = (st: Structure): { x: number; y: number } => {
  let c = centers.get(st);
  if (!c) centers.set(st, (c = hexToWorld(st.q, st.r)));
  return c;
};

/** Whether each cell of a map is inside the walls (the map never changes during a fight): 1 inside, 0 out, -1 not worked out yet. */
const insides = new WeakMap<BattleState['map'], Int8Array>();
const insideCell = (s: BattleState, cell: number): boolean => {
  let t = insides.get(s.map);
  if (!t) insides.set(s.map, (t = new Int8Array(gridW(s.map.radius) * gridW(s.map.radius)).fill(-1)));
  let v = t[cell] as number;
  if (v < 0) {
    const h = cellHex(s.map.radius, cell);
    t[cell] = v = isInside(s.map, h.q, h.r) ? 1 : 0;
  }
  return v === 1;
};

/** The structures a mangonel aims at (defenses and towers, standing or not), their centers in flat arrays; rebuilt when the list changes. */
interface LobMarks {
  len: number;
  list: Structure[];
  x: Int32Array;
  y: Int32Array;
  wall: Uint8Array;
}
const lobMarks = new WeakMap<Structure[], LobMarks>();
const marksOf = (s: BattleState): LobMarks => {
  const all = s.structures;
  let m = lobMarks.get(all);
  if (m && m.len === all.length) return m;
  const list = all.filter((st) => st.maxHp > 0 && (STRUCTURES[st.type].category === 'defense' || firesAsTower(st)));
  m = { len: all.length, list, x: new Int32Array(list.length), y: new Int32Array(list.length), wall: new Uint8Array(list.length) };
  list.forEach((st, i) => {
    const c = hexToWorld(st.q, st.r);
    m!.x[i] = c.x;
    m!.y[i] = c.y;
    m!.wall[i] = STRUCTURES[st.type].category === 'defense' ? 1 : 0;
  });
  lobMarks.set(all, m);
  return m;
};

/** A mangonel's mark: the nearest standing defense in range, else the nearest standing tower, in one pass. */
const lobTarget = (s: BattleState, e: Enemy, range: number): Structure | null => {
  const m = marksOf(s);
  let wall = -1;
  let bw = 0x7fffffff;
  let tower = -1;
  let bt = 0x7fffffff;
  const r2 = range * range;
  const ex = e.x;
  const ey = e.y;
  for (let i = 0; i < m.list.length; i++) {
    const dx = (m.x[i] as number) - ex;
    const dy = (m.y[i] as number) - ey;
    const dd = dx * dx + dy * dy;
    if (dd > r2) continue;
    if (m.wall[i]) {
      if (dd < bw && !(m.list[i] as Structure).destroyed) {
        bw = dd;
        wall = i;
      }
    } else if (dd < bt && !(m.list[i] as Structure).destroyed) {
      bt = dd;
      tower = i;
    }
  }
  return wall >= 0 ? (m.list[wall] as Structure) : tower >= 0 ? (m.list[tower] as Structure) : null;
};

const nearestWithin = (s: BattleState, e: Enemy, range: number, ok: (c: StructureCategory) => boolean): Structure | null => {
  let best: Structure | null = null;
  let bd = 0x7fffffff;
  for (const st of s.structures) {
    if (st.destroyed || st.maxHp <= 0 || !ok(STRUCTURES[st.type].category)) continue;
    const c = centerOf(st);
    const dd = dist2(e.x, e.y, c.x, c.y);
    if (dd <= range * range && dd < bd) {
      bd = dd;
      best = st;
    }
  }
  return best;
};

/**
 * Where a sapper comes up past a wall cell: the walkable ground on the far side of it (inside the walls
 * for the ring, or past a wall or gate), or null when there is none.
 */
const beyond = (s: BattleState, e: Enemy, block: Structure): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = -1;
  for (const d of DIRS) {
    const q = block.q + d.q;
    const r = block.r + d.r;
    const id = cellId(s.map.radius, q, r);
    if (id < 0 || !heroWalkable(s, id)) continue;
    const c = hexToWorld(q, r);
    // The far side: the walkable neighbour farthest from where it stands, and nearer the keep.
    const dd = dist2(e.x, e.y, c.x, c.y);
    if (hexLen(q, r) >= hexLen(block.q, block.r) && !isInside(s.map, q, r)) continue;
    if (dd > bd) {
      bd = dd;
      best = c;
    }
  }
  return best;
};
